import React, { useEffect, useRef, useState, useMemo } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { Row, Col, Card, Button, Typography, Popover, Table, Avatar, Tag, Tabs, Space, Result } from "antd";
import { PhoneOutlined, MailOutlined, UserOutlined } from "@ant-design/icons";
import log from "../../../../utils/log";
import c from "../../../../utils/common";
import moment from "moment-timezone";
import metaData from "../../../../data/metaData";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import csvUtil from "../../../../utils/csv";

//
import useAuthStore from "../../../../states/useAuth";

//
import DelaySearch from "../../../../components/Table/DelaySearch";
import ActionHeader from "../../../../components/Table/ActionHeader";
import MainTabs from "../../../../components/MerchantUser/Report/MainTabs";
import SectionHeader from "../../../../components/GetStarted/SectionHeader";
import useReport from "../../../../hooks/useQuery/useReport";
import TableColumns from "../../../../components/MerchantUser/Report/TableColumns";
import { isSuper } from "../../../../utils/permission";
import { formatTime } from "../../../../utils/time";

const { Title, Text, Paragraph } = Typography;

const csvFileName = "estamp_earn_record";

const Earn = () => {
  const history = useHistory();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  //
  const { user, auth } = useAuthStore();
  const shopIds = user?.permission?.report;

  //
  const logsQueryKey = useMemo(() => ["/report/estamp/distribute", shopIds], [shopIds]);
  const requestData = useMemo(() => {
    const data = {};
    // Conditional payload construction
    if (shopIds[0] === "all") {
      data.companyId = user.companyId;
    } else {
      data["shop.id"] = { $in: shopIds };
    }
    return data;
  }, [shopIds, user.companyId]); // Include user.companyId if needed

  const {
    data: logs = [], // Use standard data object name
    isFetching: isFetchingLogs,
    error: logsError,
    refetch,
  } = useReport(
    !!shopIds, // Enable fetching only if shopIds exist
    logsQueryKey, // Unique query key
    "reports/estamp/distribute/query", // URL for the API request
    requestData // Request payload
  );

  //Table
  const [collapsed, setCollapsed] = useState(false);
  const [searchValue, setSearchValue] = useState();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pagination, setPagination] = useState(1);
  const [filteredDataCount, setFilteredDataCount] = useState(logs.length);
  const pageRef = useRef();

  const filterData = (searchValue, data) => {
    if (!searchValue) return data;

    const lowerSearchValue = searchValue.toLowerCase();

    return data.filter((record) => {
      const id = record?.id?.toLowerCase() || "";
      return id.includes(lowerSearchValue);
    });
  };

  const filteredData = useMemo(() => {
    return logs;
  }, [logs, searchValue]);

  // Handle pagination change (page number or page size change)
  const handleTableChange = (pagination, filters, sorter, extra) => {
    const { current, pageSize } = pagination;
    console.log(current);
    setPagination(current);
    scrollToTop();

    setFilteredDataCount(extra.currentDataSource.length);
  };

  const scrollToTop = () => {
    if (pageRef.current) {
      pageRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const translations = {
    recordIdTranslation: t("DCBKrecord.recordId"),
    timeTranslation: t("DCbuy.Time"),
    statusTranslation: t("DCbuy.status"),
    assistantTranslation: t("DCbuy.assistant"),
    consumerTranslation: t("DCbuy.consumer"),
    totalAmountTranslation: t("DCbuy.totalAmount"),
  };

  const { columns } = TableColumns({
    columnOrder: ["id", "time", "coinMethod", "shop", "assistant", "client", "txTotalAmount", "coinTransactionId"],
    translations,
  });

  // Update the title of the `txTotalAmount` column
  const txTotalAmountColumn = columns.find((col) => col.dataIndex === "txTotalAmount");
  if (txTotalAmountColumn) {
    txTotalAmountColumn.title = "Paid (CAD)"; // Set the new title here
  }

  const expandedRowRender = (record) => {
    // Define the order of columns you want for the expanded table
    const { columns: itemColumns } = TableColumns({
      columnOrder: ["coin", "coinAmount"],
      translations,
    });

    return (
      <Table
        style={{
          tableLayout: "fixed",
          width: "60%",
        }}
        columns={itemColumns}
        dataSource={record.coins}
        pagination={false}
        rowKey="id"
      />
    );
  };

  const getCSVRowData = (record) => {
    const methodDisplayMap = {
      physical: "Pay 2 Earn",
      sale: "Paid at Shop",
      referrer: "Referrer",
      referee: "Referee",
    };

    return record.coins.map((coin) => ({
      [t("DCbuy.Time")]: formatTime(record?.time),
      Method: methodDisplayMap[record?.method] || record?.method,
      [t("DCBKrecord.recordId")]: record.id || "--",
      "Brand Name": coin?.brand?.name || "--",
      "Coin Period Name": coin?.period?.name || "--",
      Amount: coin.amount || "--",
      "Sale Record ID": record.transactionId || "--",
      "Shop Name": record.shop?.name || "--",
      [t("DCbuy.staffId")]: record.assistant?.email || "--",
      [t("DCredeem.consumerEmail")]: record.client?.email || "--",
      [t("DCredeem.consumerName")]: `${record.client?.nickname || ""} ${record.client?.name || ""}`.trim() || "--",
      [t("DCredeem.consumerTel")]: record.client?.tel || "--",
    }));
  };

  return user?.permission?.report != "all" ? (
    <Result
      status="403"
      title="403"
      subTitle="Sorry, you are not authorized to access this page."
      extra={
        <Button
          type="primary"
          onClick={() => {
            history.push("/login");
          }}
        >
          Back to Sign In
        </Button>
      }
    />
  ) : (
    <div>
      <Row justify="space-between" align="middle">
        <Col>
          <SectionHeader
            title={
              <Title level={3} style={{ margin: 0 }}>
                Report
              </Title>
            }
          />
        </Col>
        <Col>
          <Space>
            <Button
              size="large"
              type="primary"
              onClick={() => {
                csvUtil.exportToCSV({
                  fileName: csvFileName,
                  csvData: filteredData.flatMap(getCSVRowData),
                });
              }}
            >
              Export CSV
            </Button>
            {/* <Button size="large" onClick={() => {}}>
              Filter
            </Button> */}
          </Space>
        </Col>
      </Row>

      <MainTabs />

      <Row gutter={[24, 24]} style={{ marginTop: "2rem" }}>
        <Col span={24}>
          <ActionHeader
            pagination={pagination}
            pageSize={20}
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
            count={filteredDataCount}
            onActionClick={(value) => {
              log.debug(value);
            }}
          />
          <Table
            className="white-table-header"
            rowSelection={undefined}
            sticky
            loading={isFetchingLogs}
            columns={columns}
            dataSource={filteredData}
            pagination={{
              current: pagination,
              pageSize: 20,
              position: ["none", "bottomCenter"],
              showSizeChanger: false,
            }}
            onChange={handleTableChange}
            scroll={{ x: "100%" }} // Adjust the width as needed
            expandable={{ expandedRowRender, expandRowByClick: true }}
          />
          <Row style={{ height: "5rem" }}></Row>
        </Col>
      </Row>
    </div>
  );
};

export default Earn;
